// ABOUTME: Shipping options configuration view for webshop credentials sidepanel (relation side).
// ABOUTME: Allows adding/removing/configuring multiple shipping option records per carrier for a specific webshop.

import { Box, ButtonBase, IconButton, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import cx from "classnames";
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import { DndContext, KeyboardSensor, MouseSensor, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { tokens } from "../../../../../theme";
import { regions, countriesFromLocale } from "../../../../../utils/countries";
import { useGetWebshopShippingOptionsQuery, useSaveWebshopShippingOptionsMutation } from "../../../../../newapi/shipper/shippingOptionsSlice";
import { useGetSubscriptionQuery, useDeactivateSubscriptionMutation } from "../../../../../newapi/subscription/subscriptionSlice";
import CheckoutSubscription from "./CheckoutSubscription";
import MConfirmation from "../../../Modals/MConfirmation";
import { showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../../utils/toasts";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";
import Shbutton from "../../../../theme/buttons/Shbutton";
import Dropdown from "../../../../theme/dropout/DropOut";
import { InputDropdown } from "../../../../theme/dropdowns/InputDropdown";
import ShValidatedNumberInput from "../../../../theme/inputs/ShValidatedNumberInput";
import ShSwitch from "../../../../theme/inputs/ShSwitch";
import Text from "../../../../theme/text/Text";
import Subheading from "../../../../theme/text/Subheading";
import SpDesktopFooter from "../../../../theme/sidepanel2/SidepanelFooter";
import ShCopyButton from "../../../../theme/ShCopy";

// Shared styling for the ALL/Custom/region chips, matching the shipper checkout chips.
const regionBtnSx = (theme, colors) => ({
    px: 1.5,
    height: 28,
    borderRadius: 3,
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
    color: colors.txt['primary'],
    fontWeight: 500,
    fontFamily: "Roboto,sans-serif",
    "& .region-icon": { display: "none" },
    "&:hover": {
        background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[50],
    },
    "&.is-active": {
        background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
        borderColor: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
        color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
        "& .region-icon": { display: "inline-flex", ml: 0.5 },
    },
});

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <ButtonGroup version={2}>
            {props.onDeactivate && (
                <Shbutton
                    isLoading={props.isDeactivating}
                    className={"group-btn"}
                    onClick={props.onDeactivate}
                    color={"red"}
                    variant="contained"
                    endIcon={<BlockIcon />}
                >
                    {t('deactivate_subscription')}
                </Shbutton>
            )}
            <Shbutton
                isLoading={props.isLoading}
                className={"group-btn"}
                onClick={props.action}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                endIcon={<SaveIcon />}
            >
                {t('save')}
            </Shbutton>
        </ButtonGroup>
    );
};

const ShippingOptionRow = ({ option, index, onChange, onDelete, hasOverlap, disableDnD, colors, theme, t, countryPool }) => {
    const sortableId = option.id || option._tempId;
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: sortableId,
        disabled: disableDnD,
    });

    const sortableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 1 : "auto",
    };

    const handleFieldChange = (field, value) => {
        onChange({ ...option, [field]: value });
    };

    const language = useSelector((state) => state.settings.language);

    // The selectable pool, as country objects, in locale order.
    const poolCountries = useMemo(
        () => countriesFromLocale(language).filter(c => countryPool.includes(c.alpha2)),
        [language, countryPool]
    );

    const selected = option.selectedCountries ?? [];

    const setSelected = (codes) => {
        onChange({ ...option, selectedCountries: codes });
    };

    // InputDropdown works in indices into poolCountries. Codes saved earlier but no
    // longer in the shipper pool are dropped here (the pool is authoritative).
    const selectedIndices = selected
        .map(code => poolCountries.findIndex(c => c.alpha2 === code))
        .filter(i => i !== -1);

    const handleCountryChange = (indices) => {
        setSelected(indices.map(i => poolCountries[i].alpha2));
    };

    const selectAll = () => setSelected(poolCountries.map(c => c.alpha2));
    const clearAll = () => setSelected([]);
    const selectRegion = (regionCountries) => {
        setSelected(poolCountries.map(c => c.alpha2).filter(code => regionCountries.includes(code)));
    };
    const isRegionActive = (regionCountries) => {
        const inPool = poolCountries.map(c => c.alpha2).filter(code => regionCountries.includes(code));
        return inPool.length > 0 && inPool.every(code => selected.includes(code));
    };
    const isAllActive = poolCountries.length > 0 && selected.length === poolCountries.length;

    return (
        <Box
            ref={setNodeRef}
            {...attributes}
            style={sortableStyle}
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                p: 2,
                borderRadius: 3,
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                border: hasOverlap
                    ? `2px solid ${colors.orange[400]}`
                    : `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                    {!disableDnD && (
                        <Box
                            ref={setActivatorNodeRef}
                            {...listeners}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                                borderRadius: 2,
                                cursor: "grab",
                                color: colors.txt["secondary"],
                                touchAction: "none",
                                "&:hover": {
                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                    color: colors.txt["primary"],
                                },
                                "&:active": { cursor: "grabbing" },
                            }}
                            aria-label={t("drag_to_reorder")}
                        >
                            <DragIndicatorIcon fontSize="small" />
                        </Box>
                    )}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 24,
                            height: 24,
                            paddingX: 0.75,
                            borderRadius: 2,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            color: colors.txt["secondary"],
                            fontSize: 12,
                            fontWeight: 600,
                        }}
                    >
                        {index + 1}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Subheading>{option.shipperName}</Subheading>
                        <Text light>{t("countries")}: {selectedIndices.length}</Text>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ToggleButtonGroup
                        color={option.enabled ? "green" : "red"}
                        value={option.enabled ? "true" : "false"}
                        exclusive
                        onChange={(e) => handleFieldChange("enabled", e.target.value === "true")}
                        aria-label="Enabled"
                        className={cx(`shipping-option-toggle`, {
                            'active': option.enabled
                        })}
                        sx={{
                            background: colors.red[100],
                            padding: 0.5,
                            borderRadius: 5,
                            transition: "250ms",
                            "&.active": {
                                background: colors.green[100],
                                transition: "250ms",
                            },
                        }}
                    >
                        <ToggleButton
                            className={cx(``, { 'active': !option.enabled })}
                            sx={{
                                padding: 0,
                                paddingX: 2,
                                border: 0,
                                lineHeight: "32px",
                                textTransform: "unset",
                                borderRadius: "32px !important",
                                color: colors.green[400],
                                transition: "250ms",
                                "&.active, &.Mui-selected, &.Mui-selected:hover": {
                                    background: colors.red[400],
                                    color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                }
                            }}
                            value={"false"}>
                            {t('inactive')}
                        </ToggleButton>
                        <ToggleButton
                            className={cx(``, { 'active': option.enabled })}
                            sx={{
                                padding: 0,
                                paddingX: 2,
                                border: 0,
                                lineHeight: "32px",
                                textTransform: "unset",
                                borderRadius: "32px !important",
                                color: colors.red[400],
                                transition: "250ms",
                                "&.active,&.Mui-selected,&.Mui-selected:hover": {
                                    background: colors.green[400],
                                    color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                }
                            }}
                            value={"true"}
                        >
                            {t('active')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <IconButton
                        size="small"
                        onClick={onDelete}
                        sx={{ color: colors.red[400] }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {hasOverlap && (
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                    background: theme.palette.mode === "dark" ? colors.orange[100] : colors.orange[100],
                }}>
                    <WarningAmberIcon fontSize="small" sx={{ color: colors.orange[400] }} />
                    <Text sx={{ color: colors.orange[400], fontSize: 13 }}>
                        {t("shipping_option_weight_overlap")}
                    </Text>
                </Box>
            )}

            {option.enabled && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
                    <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        <Box lineHeight={"38px"}>
                            <Text>{t("price")} (€)</Text>
                            <ShValidatedNumberInput
                                name="price"
                                value={option.price ?? 0}
                                onChange={(name, val) => handleFieldChange(name, parseFloat(val) || 0)}
                                btnStep={0.05}
                                step={0.01}
                            />
                        </Box>
                        <Box lineHeight={"38px"}>
                            <Text>{t("free_above_order_price")} (€)</Text>
                            <ShValidatedNumberInput
                                name="freeAboveOrderPrice"
                                value={option.freeAboveOrderPrice ?? ""}
                                onChange={(name, val) => handleFieldChange(name, val === "" || val === null ? null : parseFloat(val) || 0)}
                                btnStep={1}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        <Box lineHeight={"38px"}>
                            <Text>{t("min_weight")} ({t("gram")})</Text>
                            <ShValidatedNumberInput
                                name="minWeight"
                                value={option.minWeight ?? 0}
                                onChange={(name, val) => handleFieldChange(name, parseInt(val) || 0)}
                            />
                        </Box>
                        <Box lineHeight={"38px"}>
                            <Text>{t("max_weight")} ({t("gram")})</Text>
                            <ShValidatedNumberInput
                                name="maxWeight"
                                value={option.maxWeight ?? 0}
                                onChange={(name, val) => handleFieldChange(name, parseInt(val) || 0)}
                            />
                        </Box>
                    </Box>
                    {option.supportsDeliveryDates && (
                        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <ShSwitch
                                    checked={option.deliveryDatesEnabled === true}
                                    onChange={(e) => handleFieldChange("deliveryDatesEnabled", e.target.checked)}
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                />
                                <Text>{t("offer_delivery_dates")}</Text>
                            </Box>
                            {option.deliveryDatesEnabled === true && (
                                <Box lineHeight={"38px"}>
                                    <Text>{t("delivery_days_ahead")}</Text>
                                    <ShValidatedNumberInput
                                        name="deliveryDaysAhead"
                                        value={option.deliveryDaysAhead ?? 5}
                                        onChange={(name, val) => handleFieldChange(name, val === "" || val === null ? "" : Math.min(10, Math.max(1, parseInt(val) || 5)))}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}>
                        <Text>{t("checkout_countries")}</Text>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <ButtonBase
                                className={cx(undefined, { 'is-active': isAllActive })}
                                onClick={selectAll}
                                sx={regionBtnSx(theme, colors)}
                            >
                                {t("all")}
                                <CheckIcon fontSize='small' className="region-icon" />
                            </ButtonBase>
                            <ButtonBase
                                className={cx(undefined, { 'is-active': selected.length === 0 })}
                                onClick={clearAll}
                                sx={regionBtnSx(theme, colors)}
                            >
                                {t("custom")}
                                <CheckIcon fontSize='small' className="region-icon" />
                            </ButtonBase>
                            {regions
                                .filter(r => r.name !== "Worldwide")
                                .map((region, idx) => (
                                    <ButtonBase
                                        key={idx}
                                        className={cx(undefined, { 'is-active': isRegionActive(region.countries) })}
                                        onClick={() => selectRegion(region.countries)}
                                        sx={regionBtnSx(theme, colors)}
                                    >
                                        {region.name}
                                        <CheckIcon fontSize='small' className="region-icon" />
                                    </ButtonBase>
                                ))}
                        </Box>
                        <Box sx={{ maxWidth: 360 }}>
                            <InputDropdown
                                name={`countries_${option.id || option._tempId}`}
                                multiple={true}
                                noSelection={t('choose_countries')}
                                displayName="name"
                                searchOptions={{ enabled: true, placeholder: t("search") }}
                                options={poolCountries}
                                selected={selectedIndices}
                                onChange={handleCountryChange}
                                closeOnSelection={false}
                            />
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

let tempIdCounter = 0;

const ShippingOptions = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { data: shippingOptionsData, isLoading: isLoadingOptions } = useGetWebshopShippingOptionsQuery(
        props.webshopCredentialsId,
        { skip: !props.webshopCredentialsId }
    );

    // Checkout is only available with an active checkout subscription. Without one,
    // the activation panel replaces the shipper/price config below.
    const { data: subscription, isLoading: isLoadingSubscription } = useGetSubscriptionQuery(
        { externalId: props.webshopCredentialsId, type: 'checkout' },
        { skip: !props.webshopCredentialsId }
    );
    const hasActiveSubscription = !!subscription?.active;

    const [saveShippingOptions, { isLoading: isSaving }] = useSaveWebshopShippingOptionsMutation();
    const [deactivateSubscription, { isLoading: isDeactivating }] = useDeactivateSubscriptionMutation();
    const [openDeactivateConfirm, setOpenDeactivateConfirm] = useState(false);

    const [options, setOptions] = useState([]);

    useEffect(() => {
        if (shippingOptionsData?.shippingOptions) {
            setOptions(shippingOptionsData.shippingOptions.map(opt => ({
                ...opt,
                // allowedCountries = the shipper pool; selectedCountries = the chosen subset.
                selectedCountries: opt.allowedCountries ?? [],
                supportsDeliveryDates: opt.supportsDeliveryDates || false,
                deliveryDatesEnabled: opt.deliveryDatesEnabled === true,
                deliveryDaysAhead: opt.deliveryDaysAhead ?? 5,
            })));
        }
    }, [shippingOptionsData]);

    const availableShippers = shippingOptionsData?.availableShippers ?? [];
    const apiKey = shippingOptionsData?.apiKey;

    const poolByCarrier = useMemo(() => {
        const map = {};
        for (const s of availableShippers) {
            map[s.carrierCode] = s.allowedCountries || [];
        }
        return map;
    }, [availableShippers]);

    const shipperDropdownOptions = useMemo(() => {
        return availableShippers.map(s => ({
            ...s,
            displayName: s.name,
        }));
    }, [availableShippers]);

    const handleAddShipper = (selectedIndex) => {
        if (selectedIndex === undefined || selectedIndex === null) return;
        const shipper = availableShippers[selectedIndex];
        if (!shipper) return;

        tempIdCounter += 1;
        const newOption = {
            _tempId: `new_${tempIdCounter}`,
            id: null,
            shipperId: shipper.id,
            carrierCode: shipper.carrierCode,
            shipperName: shipper.name,
            allowedCountries: shipper.allowedCountries || [],
            selectedCountries: shipper.allowedCountries || [],
            supportsServicePoints: shipper.supportsServicePoints || false,
            supportsDeliveryDates: shipper.supportsDeliveryDates || false,
            deliveryDatesEnabled: false,
            deliveryDaysAhead: 5,
            enabled: true,
            price: 0,
            freeAboveOrderPrice: null,
            minWeight: 0,
            maxWeight: 31500,
        };
        setOptions(prev => [...prev, newOption]);
    };

    const handleOptionChange = (index, updatedOption) => {
        setOptions(prev => {
            const updated = [...prev];
            updated[index] = updatedOption;
            return updated;
        });
    };

    const handleDeleteOption = (index) => {
        setOptions(prev => prev.filter((_, i) => i !== index));
    };

    const overlappingIndices = useMemo(() => {
        const overlaps = new Set();
        for (let i = 0; i < options.length; i++) {
            for (let j = i + 1; j < options.length; j++) {
                if (options[i].carrierCode !== options[j].carrierCode) continue;
                if (!options[i].enabled || !options[j].enabled) continue;
                const aMin = options[i].minWeight ?? 0;
                const aMax = options[i].maxWeight ?? Infinity;
                const bMin = options[j].minWeight ?? 0;
                const bMax = options[j].maxWeight ?? Infinity;
                if (aMin <= bMax && bMin <= aMax) {
                    overlaps.add(i);
                    overlaps.add(j);
                }
            }
        }
        return overlaps;
    }, [options]);

    const handleSaveClick = useCallback(async () => {
        try {
            if (isSaving) return;
            const payload = options.map((opt, index) => ({
                id: opt.id || null,
                carrierCode: opt.carrierCode,
                enabled: opt.enabled,
                allowedCountries: opt.selectedCountries ?? [],
                price: opt.price,
                freeAboveOrderPrice: opt.freeAboveOrderPrice,
                minWeight: opt.minWeight,
                maxWeight: opt.maxWeight,
                deliveryDatesEnabled: opt.deliveryDatesEnabled === true,
                deliveryDaysAhead: Number.isInteger(opt.deliveryDaysAhead) ? opt.deliveryDaysAhead : 5,
                sortOrder: index,
            }));
            const response = await saveShippingOptions({
                webshopCredentialId: props.webshopCredentialsId,
                shippingOptions: payload,
            }).unwrap();
            showToastMessageUpdateRequest(t, response);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    }, [isSaving, options, props.webshopCredentialsId, saveShippingOptions, t]);

    const handleDeactivateClick = useCallback(async () => {
        try {
            if (isDeactivating || !subscription?.id) return;
            const response = await deactivateSubscription({
                id: subscription.id,
                externalId: props.webshopCredentialsId,
            }).unwrap();
            if (response?.id) {
                showToastMessageUpdateRequest(t, { success: true });
            } else {
                showToastMessageRequestError(t("update_failed"), t, { data: response });
            }
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        } finally {
            setOpenDeactivateConfirm(false);
        }
    }, [isDeactivating, subscription?.id, props.webshopCredentialsId, deactivateSubscription, t]);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions: <GridActions
                isLoading={isSaving}
                action={handleSaveClick}
                isDeactivating={isDeactivating}
                onDeactivate={() => setOpenDeactivateConfirm(true)}
            />,
        });

        return () => {};
    }, [props.webshopCredentialsId, handleSaveClick, isSaving, isDeactivating]);

    const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 2 } });
    const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 10 } });
    const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } });
    const keyboardSensor = useSensor(KeyboardSensor);
    const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor, pointerSensor);

    const isMobileWidth = useMediaQuery("(max-width:47.9375em)");
    // Touchscreen laptops, pen tablets and remote desktop sessions report touch support while
    // still being driven by a mouse, so a fine primary pointer keeps drag and drop available.
    const hasFinePointer = useMediaQuery("(pointer: fine)");
    const [disableDnD, setDisableDnD] = useState(false);
    useEffect(() => {
        const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
        setDisableDnD(isMobileWidth || (isTouchDevice && !hasFinePointer));
    }, [isMobileWidth, hasFinePointer]);

    const sortableItems = useMemo(
        () => options.map(opt => opt.id || opt._tempId),
        [options]
    );

    const onDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setOptions(prev => {
            const oldIndex = prev.findIndex(opt => (opt.id || opt._tempId) === active.id);
            const newIndex = prev.findIndex(opt => (opt.id || opt._tempId) === over.id);
            if (oldIndex === -1 || newIndex === -1) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    // No active checkout subscription: replace the config with the activation panel.
    if (!isLoadingSubscription && !hasActiveSubscription) {
        return (
            <CheckoutSubscription
                config={props.config}
                webshopCredentialsId={props.webshopCredentialsId}
                replaceView={props.replaceView}
            />
        );
    }

    return (
        <Box height={1} display={"flex"} flexDirection={"column"} paddingX={3}
            sx={{
                "@media screen and (min-width: 48.1em)": {
                    marginBottom: 11
                }
            }}
        >
            {apiKey && (
                <Dropdown title={t("api_key")} open>
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        paddingTop: 2,
                        paddingBottom: 1,
                    }}>
                        <Box
                            sx={{
                                flex: 1,
                                px: 2,
                                py: 1,
                                borderRadius: 3,
                                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                                border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                fontFamily: "monospace",
                                fontSize: 13,
                                wordBreak: "break-all",
                                color: colors.txt["primary"],
                            }}
                        >
                            {apiKey}
                        </Box>
                        <ShCopyButton value={apiKey} />
                    </Box>
                    {props.webshopType === "woocommerce" && (
                        <Box sx={{ paddingTop: 1, paddingBottom: 1 }}>
                            <Text light>
                                {t("woocommerce_plugin_instructions")}{" "}
                                <Box
                                    component="a"
                                    href="https://www.stockhub.nl/stockhub-shipping.zip"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: colors.blue[400], fontWeight: 600, textDecoration: "none" }}
                                >
                                    stockhub-shipping.zip
                                </Box>
                            </Text>
                        </Box>
                    )}
                </Dropdown>
            )}

            <Dropdown title={t("shipping_options")} open>
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    paddingTop: 3,
                }}>
                    {isLoadingOptions && <Text>{t("loading")}</Text>}
                    {!isLoadingOptions && options.length === 0 && (
                        <Text light>{t("no_shipping_options_available")}</Text>
                    )}
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={onDragEnd}
                    >
                        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {options.map((option, index) => (
                                    <ShippingOptionRow
                                        key={option.id || option._tempId}
                                        option={option}
                                        index={index}
                                        onChange={(updated) => handleOptionChange(index, updated)}
                                        onDelete={() => handleDeleteOption(index)}
                                        hasOverlap={overlappingIndices.has(index)}
                                        disableDnD={disableDnD}
                                        colors={colors}
                                        theme={theme}
                                        t={t}
                                        countryPool={poolByCarrier[option.carrierCode] ?? option.allowedCountries ?? []}
                                    />
                                ))}
                            </Box>
                        </SortableContext>
                    </DndContext>

                    {availableShippers.length > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pt: 1 }}>
                            <InputDropdown
                                name="addShipper"
                                displayName="displayName"
                                noSelection={t("add_shipping_option")}
                                options={shipperDropdownOptions}
                                selected={null}
                                onChange={handleAddShipper}
                            />
                        </Box>
                    )}
                </Box>
            </Dropdown>

            <SpDesktopFooter>
                <GridActions
                    isLoading={isSaving}
                    action={handleSaveClick}
                    isDeactivating={isDeactivating}
                    onDeactivate={() => setOpenDeactivateConfirm(true)}
                />
            </SpDesktopFooter>

            <MConfirmation
                open={openDeactivateConfirm}
                handleClose={() => setOpenDeactivateConfirm(false)}
                confirm={handleDeactivateClick}
                ignoreCloseAfterConfirm
                title={t('confirm_deactivate_subscription_title')}
                subtitle={t('confirm_deactivate_subscription_description')}
                content={<Text>{t('confirm_deactivate_subscription_content')}</Text>}
            />
        </Box>
    );
};

export default ShippingOptions;
